import type { AppTranslator } from "@i18n/next";
import {
  BROADCAST_CANNOT_BE_SCHEDULED_IN_PAST,
  BROADCAST_NOT_SCHEDULED,
  isRequestError,
  USER_NOT_BROADCAST_CREATOR,
} from "@packages/sdk";

import { formatShortDateLabel } from "./events";

/**
 * What to show an admin when a broadcast write fails.
 *
 * The three coded failures are all reachable from a page whose data has gone
 * stale — the schedule passed, the poller delivered the broadcast, or the
 * admin lost their claim on it — so each says what actually happened rather
 * than surfacing the raw server message.
 */
export const getBroadcastErrorDescription = (
  error: unknown,
  t: AppTranslator,
): string => {
  if (isRequestError(error)) {
    switch (error.code) {
      case BROADCAST_CANNOT_BE_SCHEDULED_IN_PAST:
        return t("general_words_date_cannot_be_in_the_past");
      case BROADCAST_NOT_SCHEDULED:
        return t("dashboard_broadcast_error_already_sent");
      case USER_NOT_BROADCAST_CREATOR:
        return t("dashboard_broadcast_error_not_creator");
    }
  }

  return error instanceof Error ? error.message : String(error);
};

/**
 * Whether `userId` may edit or send `broadcast` now. The backend rejects
 * anyone but the creator with `UserNotBroadcastCreator`, so the menu hides
 * those actions rather than letting an admin walk into a 403. Deleting is
 * deliberately not gated: an admin who has since been removed must not be able
 * to strand broadcasts nobody else can stop.
 */
export const getIsBroadcastCreator = ({
  broadcasterUserId,
  userId,
}: {
  broadcasterUserId?: number | null;
  userId?: number | null;
}): boolean =>
  broadcasterUserId != null && userId != null && broadcasterUserId === userId;

/**
 * The design's schedule stamp, e.g. "Tue, Jul 7 at 9:00 AM". The connector is
 * a translated key rather than a literal, and the time comes from the reader's
 * locale, so callers must not render it during SSR — it would not match what
 * the client produces in their timezone.
 */
export const formatBroadcastScheduleLabel = (
  date: Date,
  t: AppTranslator,
): string =>
  t("dashboard_broadcast_scheduled_at", {
    0: formatShortDateLabel(date),
    1: date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }),
  });
